/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Image } from '@fluentui/react/lib/Image';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { GlobalHeaderLinks } from 'src/appShell/globalHeader/utils/globalHeaderLinks';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logNextSteps } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { nextStepsAskQnALink, nextStepsSupportAndHelp } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';

type NextStepsSupportScreenProps = { isResultsEmpty: boolean };

const messages = defineMessages({
    azureImageAlt: { id: 'languageStudio.nextStepsSupportScreen.azureImageAlt', defaultMessage: 'Azure icon' },
    title: { id: 'languageStudio.nextStepsSupportScreen.title', defaultMessage: 'Azure Cognitive Services support' },
    description: { id: 'languageStudio.nextStepsSupportScreen.description', defaultMessage: 'Explore the range of' },
    askQuestions: { id: 'languageStudio.nextStepsSupportScreen.askQuestions', defaultMessage: 'Ask questions on Microsoft Q&A' },
    createSupportReq: { id: 'languageStudio.nextStepsSupportScreen.createSupportReq', defaultMessage: 'Create Azure support request' },
    followCommunity: {
        id: 'languageStudio.nextStepsSupportScreen.followCommunity',
        defaultMessage: 'Stay up-to-date with Cognitive Services Tech Community blog'
    },
    otherSupportOptions: {
        id: 'languageStudio.nextStepsSupportScreen.otherSupportOptions',
        defaultMessage: 'See other support and help options'
    },
    azureSupportOptions: {
        id: 'languageStudio.nextStepsSupportScreen.azureSupportOptions',
        defaultMessage: 'Azure support options and choose the plan'
    },
    azureSupportOptionsCont: {
        id: 'languageStudio.nextStepsSupportScreen.azureSupportOptionsCont',
        defaultMessage: `that best fits, whether you're a developer just starting your cloud journey or a large organization
    deploying business-critical, strategic applications. Azure customers can create and manage support requests
    in the Azure portal.`
    }
});

const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { fontSize: '14px' } });

export const NextStepsSupportScreen = ({ isResultsEmpty }: NextStepsSupportScreenProps) => {
    const { localize } = useLocalization();

    const handleClickLink = () => {
        if (!isResultsEmpty) {
            logNextSteps();
        }
    };

    return (
        <Stack tokens={{ childrenGap: 25 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <Image src={getAssetsUrl('assets/images/languageStudio/azureIcon.svg')} alt={localize(messages.azureImageAlt)} />

                <LuisText as="h3" fontWeight={600}>
                    {localize(messages.title)}
                </LuisText>
                <LuisText>
                    {localize(messages.description)}&nbsp;
                    <ExternalLink href={GlobalHeaderLinks.azureSupportLink} onClick={handleClickLink}>
                        <Stack horizontal verticalAlign="baseline">
                            {localize(messages.azureSupportOptions)}&nbsp;
                            <Icon iconName="MiniExpand" styles={iconStyles} />
                        </Stack>
                    </ExternalLink>
                    &nbsp;{localize(messages.azureSupportOptionsCont)}
                </LuisText>
                <ExternalLink href={GlobalHeaderLinks.requestTicketLink} onClick={handleClickLink}>
                    <LuisText>{localize(messages.createSupportReq)}</LuisText>
                </ExternalLink>
            </Stack>
            <Stack tokens={{ childrenGap: 8 }}>
                <ExternalLink href={nextStepsAskQnALink} onClick={handleClickLink}>
                    <LuisText>{localize(messages.askQuestions)}</LuisText>
                </ExternalLink>

                <ExternalLink href={GlobalHeaderLinks.azureBlogLink} onClick={handleClickLink}>
                    <LuisText>{localize(messages.followCommunity)}</LuisText>
                </ExternalLink>

                <ExternalLink href={nextStepsSupportAndHelp} onClick={handleClickLink}>
                    <Stack horizontal verticalAlign="baseline">
                        <LuisText>{localize(messages.otherSupportOptions)}</LuisText>&nbsp;
                        <Icon iconName="MiniExpand" styles={iconStyles} />
                    </Stack>
                </ExternalLink>
            </Stack>
        </Stack>
    );
};
